import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { Chip } from '@/components/atoms';
import { useProfileState } from '@/contexts/ProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  ORG_TYPES, CAREER_STAGES, EDUCATION_LEVELS, FIELDS_OF_STUDY,
  SKILLS_REPORTING, SKILLS_ANALYSIS, SKILLS_LEADERSHIP,
  TRANSFERABLE_SKILLS, ACADEMIC_SKILLS, ACADEMIC_TRIGGERS,
  DOMAINS, FUNCTIONS, DOMAIN_FRAMEWORKS, SUSTAINABILITY_DOMAINS,
} from '@/lib/constants';

export default function Step1Confirm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { draft, patchDraft, savedProfile } = useProfileState();
  // `hydrated` flips true after the profile draft/savedProfile has been merged
  // into local form state. Until then we paint a skeleton for the preview card
  // so the layout doesn't jump when data lands one tick later.
  const [hydrated, setHydrated] = useState(false);
  const [form, setForm] = useState({
    name: '', title: '', location: '',
    org_type: '', career_stage: '',
    years_in_sustainability: 0,
    education_level: '', field_of_study: '',
    domain: '', domain_detected: false,
    // NEW: function axis (the work you do) — separate from domain (subject).
    // See spec Part 1. `function_detected` mirrors `domain_detected` and is
    // set true when the CV extractor filled it in.
    function: '', function_detected: false,
    skills_reporting: [], skills_analysis: [], skills_leadership: [],
    skills_domain: [],
    transferable_skills: [], academic_skills: [],
    additional_skills: '',
    recent_roles: [],
  });

  useEffect(() => {
    const src = { ...(savedProfile || {}), ...draft };
    setForm((f) => ({
      ...f,
      ...src,
      name: src.name || user?.name || f.name,
      skills_reporting: src.skills_reporting || [],
      skills_analysis: src.skills_analysis || [],
      skills_leadership: src.skills_leadership || [],
      skills_domain: src.skills_domain || [],
      transferable_skills: src.transferable_skills || [],
      academic_skills: src.academic_skills || [],
      recent_roles: Array.isArray(src.recent_roles) ? src.recent_roles.slice(0, 3) : [],
      domain: src.domain || '',
      // `domain_detected` is true if the domain came from the CV extractor (not user typed)
      domain_detected: Boolean(src.domain && (src.domain_detected ?? true)),
      function: src.function || '',
      function_detected: Boolean(src.function && (src.function_detected ?? true)),
      // PRE-SELECT frameworks detected in the CV (spec Part 3). Merge with any
      // domain skills the user has already picked so we don't clobber their edits.
      skills_domain: Array.from(new Set([
        ...(src.skills_domain || []),
        ...(src.detected_frameworks || []),
      ])),
    }));
    setHydrated(true);
  }, [draft, savedProfile, user]);

  const showAcademic = useMemo(() => ACADEMIC_TRIGGERS.includes(form.education_level), [form.education_level]);
  const isSustainabilityDomain = useMemo(
    () => SUSTAINABILITY_DOMAINS.has(form.domain),
    [form.domain]
  );
  const domainFrameworkChips = useMemo(
    () => DOMAIN_FRAMEWORKS[form.domain] || [],
    [form.domain]
  );

  const toggle = (key, v) => {
    setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter(x => x !== v) : [...f[key], v] }));
  };

  const next = () => {
    if (!form.name.trim()) { alert('Please enter your name.'); return; }
    if (!form.title.trim()) { alert('Please enter your current job title.'); return; }
    if (!form.domain) { alert('Please select your domain.'); return; }
    if (!form.function) { alert('Please select your focus.'); return; }
    patchDraft(form);
    navigate('/onboarding/pivot');
  };

  return (
    <OnboardingLayout step={1}>
      <p className="text-[12px] uppercase tracking-[0.14em] text-sd-mid-gray font-medium">Step 2 of 4</p>
      <h2 className="text-[36px] font-semibold text-sd-black mt-2 mb-3 heading-tight">Confirm your profile</h2>
      <p className="text-sd-mid-gray text-[15px] mb-6 max-w-[560px] leading-relaxed">
        We pre-filled what we could pull. Tweak anything below — the more accurate, the better your analysis.
      </p>

      {/* Extraction preview — first 3 roles found. Gives the user a quick trust
          check on what we parsed before they scroll through the whole form.
          While the profile draft/savedProfile is still hydrating we paint a
          skeleton so the layout doesn't jump. */}
      {!hydrated ? (
        <div
          className="rounded-card p-5 mb-8 border border-sd-light-gray shadow-card animate-pulse"
          style={{ background: 'linear-gradient(135deg, #FAFCFB 0%, #FFFFFF 60%)' }}
          data-testid="confirm-preview-skeleton"
          aria-hidden="true"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-sd-light-gray shrink-0" />
            <span className="h-3 w-40 rounded bg-sd-light-gray" />
          </div>
          <div className="mb-4 space-y-2">
            <div className="h-5 w-52 rounded bg-sd-light-gray" />
            <div className="h-3 w-64 rounded bg-sd-light-gray" />
          </div>
          <div className="h-2.5 w-36 rounded bg-sd-light-gray mb-2" />
          <ol className="space-y-2 mb-3">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-baseline gap-3">
                <span className="w-[18px] h-[18px] rounded-full bg-sd-light-gray shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 rounded bg-sd-light-gray" style={{ width: `${72 - i * 12}%` }} />
                  <div className="h-2.5 w-24 rounded bg-sd-light-gray" />
                </div>
              </li>
            ))}
          </ol>
        </div>
      ) : (form.recent_roles?.length > 0 || form.name || form.title) && (
        <div
          className="rounded-card p-5 mb-8 border border-[rgba(76,217,100,0.28)] shadow-card"
          style={{ background: 'linear-gradient(135deg, #F5FFF8 0%, #FFFFFF 60%)' }}
          data-testid="confirm-extraction-preview"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-sd-primary text-white flex items-center justify-center shrink-0">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <div className="text-[11.5px] uppercase tracking-[0.14em] text-sd-primary-dark font-semibold">We found your profile</div>
          </div>

          <div className="mb-4">
            <div className="text-[22px] font-semibold text-sd-black leading-tight">{form.name || '—'}</div>
            <div className="text-[14px] text-sd-dark-gray mt-0.5">{form.title || 'Title to confirm below'}</div>
          </div>

          {form.recent_roles?.length > 0 && (
            <>
              <div className="text-[11.5px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold mb-2">Recent roles detected</div>
              <ol className="space-y-1.5 mb-3">
                {form.recent_roles.slice(0, 3).map((r, i) => (
                  <li key={i} className="flex items-baseline gap-3" data-testid={`preview-role-${i}`}>
                    <span className="w-[18px] h-[18px] rounded-full bg-white border border-sd-light-gray text-[10.5px] font-semibold text-sd-black flex items-center justify-center shrink-0 tabular-nums">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] text-sd-black">
                        <span className="font-semibold">{r.title || 'Role'}</span>
                        {r.company && <span className="text-sd-dark-gray"> · {r.company}</span>}
                      </div>
                      {r.dates && <div className="text-[11.5px] text-sd-mid-gray">{r.dates}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </>
          )}

          <div className="text-[12.5px] text-sd-mid-gray flex items-start gap-1.5 mt-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="mt-0.5 shrink-0">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Something off? Just edit any field below — everything's yours to change.</span>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Field label="Name">
          <input className="sd-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="confirm-name" />
        </Field>
        <Field label="Current job title">
          <input className="sd-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="confirm-title" />
        </Field>
        <Field label="Location">
          <input className="sd-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="confirm-location" placeholder="City, Country" />
        </Field>
        <Field label="Organisation type">
          <select className="sd-input" value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })} data-testid="confirm-org">
            <option value="">Select…</option>
            {ORG_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Career stage">
          <select className="sd-input" value={form.career_stage} onChange={(e) => setForm({ ...form, career_stage: e.target.value })} data-testid="confirm-stage">
            <option value="">Select…</option>
            {CAREER_STAGES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label={`Years in sustainability / energy — ${form.years_in_sustainability}`}>
          <input type="range" min={0} max={20} value={form.years_in_sustainability} onChange={(e) => setForm({ ...form, years_in_sustainability: Number(e.target.value) })} className="w-full accent-sd-primary" data-testid="confirm-years" />
          <div className="flex justify-between text-[11px] text-sd-mid-gray mt-1"><span>0</span><span>10</span><span>20+</span></div>
          <p className="text-[11px] text-sd-mid-gray mt-1 leading-snug">
            Includes time spent at an energy company (utility, renewables, grid, cleantech), in EHS, or in any explicit sustainability / ESG / CSR role.
          </p>
        </Field>
        <Field label="Highest education">
          <select className="sd-input" value={form.education_level} onChange={(e) => setForm({ ...form, education_level: e.target.value })} data-testid="confirm-edu-level">
            <option value="">Select…</option>
            {EDUCATION_LEVELS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Field of study">
          <select className="sd-input" value={form.field_of_study} onChange={(e) => setForm({ ...form, field_of_study: e.target.value })} data-testid="confirm-edu-field">
            <option value="">Select…</option>
            {FIELDS_OF_STUDY.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Your domain">
          <select
            className="sd-input"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value, domain_detected: false })}
            data-testid="confirm-domain"
          >
            <option value="">Select your domain…</option>
            {DOMAINS.map(d => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          {form.domain && form.domain_detected && (
            <p className="text-[11px] text-sd-mid-gray mt-1 flex items-center gap-1" data-testid="confirm-domain-detected">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Detected from your profile.
            </p>
          )}
          {form.domain && (
            <p className="text-[11px] text-sd-mid-gray mt-1 leading-snug">
              {DOMAINS.find(d => d.id === form.domain)?.description}
            </p>
          )}
        </Field>
        <Field label="Your focus">
          <select
            className="sd-input"
            value={form.function}
            onChange={(e) => setForm({ ...form, function: e.target.value, function_detected: false })}
            data-testid="confirm-function"
          >
            <option value="">Select your focus…</option>
            {FUNCTIONS.map(f => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          {form.function && form.function_detected && (
            <p className="text-[11px] text-sd-mid-gray mt-1 flex items-center gap-1" data-testid="confirm-function-detected">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Detected from your profile.
            </p>
          )}
          {form.function && (
            <p className="text-[11px] text-sd-mid-gray mt-1 leading-snug">
              {FUNCTIONS.find(f => f.id === form.function)?.description}
            </p>
          )}
        </Field>
      </div>

      {/* Domain-adaptive skill chips.
          - Sustainability domains show a single "Frameworks in {domain}" group with the domain's specific chips.
          - Outside domains skip framework chips entirely — only transferable skills are shown (per spec §4).
          - Legacy general groups (Reporting / Analysis / Leadership) are kept as a fallback when no domain is set. */}
      {form.domain && domainFrameworkChips.length > 0 ? (
        <SkillGroup
          title={`Frameworks & tools in ${DOMAINS.find(d => d.id === form.domain)?.label || 'your field'}`}
          subtitle="Tap any you have hands-on experience with."
          skills={domainFrameworkChips}
          selected={form.skills_domain}
          onToggle={(s) => toggle('skills_domain', s)}
        />
      ) : (!form.domain || isSustainabilityDomain) ? (
        <>
          <SkillGroup title="Reporting & Frameworks" subtitle="Tap any you have hands-on experience with." skills={SKILLS_REPORTING} selected={form.skills_reporting} onToggle={(s) => toggle('skills_reporting', s)} />
          <SkillGroup title="Analysis & Strategy" skills={SKILLS_ANALYSIS} selected={form.skills_analysis} onToggle={(s) => toggle('skills_analysis', s)} />
          <SkillGroup title="Leadership & Communication" skills={SKILLS_LEADERSHIP} selected={form.skills_leadership} onToggle={(s) => toggle('skills_leadership', s)} />
        </>
      ) : null}
      <SkillGroup
        title="Transferable skills"
        subtitle={isSustainabilityDomain
          ? "From other careers — we surface these as hidden assets."
          : "These translate directly into sustainability. Tap any you have."}
        skills={TRANSFERABLE_SKILLS}
        selected={form.transferable_skills}
        onToggle={(s) => toggle('transferable_skills', s)}
        variant="amber"
      />
      {showAcademic && (
        <SkillGroup title="Academic skills" subtitle="Shown because you selected Master's, PhD or Currently enrolled." skills={ACADEMIC_SKILLS} selected={form.academic_skills} onToggle={(s) => toggle('academic_skills', s)} variant="purple" />
      )}

      <div className="mb-10">
        <label className="block text-[13px] font-semibold text-sd-black mb-1.5">Add a skill we missed</label>
        <input className="sd-input" placeholder="e.g., Biodiversity accounting, Python, GIS, SAP…" value={form.additional_skills} onChange={(e) => setForm({ ...form, additional_skills: e.target.value })} data-testid="confirm-additional-skills" />
      </div>

      <div className="flex items-center justify-between">
        <button className="sd-btn-secondary" onClick={() => navigate('/onboarding/connect')} data-testid="confirm-back">← Back</button>
        <button className="sd-btn-primary px-8" onClick={next} data-testid="confirm-next">Continue →</button>
      </div>
    </OnboardingLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[13px] font-semibold text-sd-black mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function SkillGroup({ title, subtitle, skills, selected, onToggle, variant }) {
  return (
    <div className="mb-7">
      <h3 className="text-[14px] font-semibold text-sd-black">{title}</h3>
      {subtitle && <p className="text-sd-mid-gray text-[12px] mb-2.5">{subtitle}</p>}
      <div className="flex flex-wrap gap-2">
        {skills.map((s) => (
          <Chip key={s} on={selected.includes(s)} onClick={() => onToggle(s)} variant={variant}>{s}</Chip>
        ))}
      </div>
    </div>
  );
}
